import { Injectable } from '@angular/core'
import { Rect, ResizeHandle, Handle } from '../types/cropper.type'

@Injectable()
export class CropperService {
  private readonly HANDLE_SIZE = 25

  /**
   * Get scaled coordinates from a mouse or touch event
   */
  getScaledCoordinates(event: MouseEvent | TouchEvent, canvas: HTMLCanvasElement): { scaledX: number; scaledY: number } {
    const rect = canvas.getBoundingClientRect()
    let clientX: number
    let clientY: number

    // Desktop Safari does not define TouchEvent; `instanceof TouchEvent` throws there.
    if ('touches' in event) {
      const touch = event.touches[0] || event.changedTouches[0]
      if (!touch) return { scaledX: 0, scaledY: 0 }
      clientX = touch.clientX
      clientY = touch.clientY
    } else {
      clientX = event.clientX
      clientY = event.clientY
    }

    const x = clientX - rect.left
    const y = clientY - rect.top
    return { scaledX: x, scaledY: y }
  }

  /**
   * Detect which handle (if any) is at the given coordinates
   */
  detectHandle(x: number, y: number, box: Rect): ResizeHandle | null {
    const cornerHandles = this._getCornerHandles(box)
    for (const handle of cornerHandles) {
      if (this._isPointInHandle(x, y, handle, this.HANDLE_SIZE)) return handle.name
    }

    const midpointHandles = this._getMidpointHandles(box)
    for (const handle of midpointHandles) {
      if (this._isPointInMidpointHandle(x, y, handle)) return handle.name
    }

    return null
  }

  /**
   * Check if a point is within the crop box
   */
  isPointInBox(x: number, y: number, box: Rect): boolean {
    return x >= box.x && x <= box.x + box.width && y >= box.y && y <= box.y + box.height
  }

  /**
   * Get the appropriate cursor for a given point
   */
  getCursor(x: number, y: number, box: Rect): string {
    const cornerHandles = this._getCornerHandles(box)
    for (const handle of cornerHandles) {
      if (this._isPointInHandle(x, y, handle, this.HANDLE_SIZE)) {
        return handle.name === 'nw' || handle.name === 'se' ? 'nwse-resize' : 'nesw-resize'
      }
    }

    const midpointHandles = this._getMidpointHandles(box)
    for (const handle of midpointHandles) {
      if (this._isPointInMidpointHandle(x, y, handle)) {
        return handle.name === 'n' || handle.name === 's' ? 'ns-resize' : 'ew-resize'
      }
    }

    return 'move'
  }

  /**
   * Resize the crop box based on handle and mouse position
   */
  resizeCropBox(box: Rect, handle: ResizeHandle, x: number, y: number, aspectRatio: number): Rect {
    const handlers: Record<ResizeHandle, (box: Rect, x: number, y: number, ratio: number) => Rect> = {
      se: this._resizeSoutheast.bind(this),
      sw: this._resizeSouthwest.bind(this),
      ne: this._resizeNortheast.bind(this),
      nw: this._resizeNorthwest.bind(this),
      s: this._resizeSouth.bind(this),
      n: this._resizeNorth.bind(this),
      e: this._resizeEast.bind(this),
      w: this._resizeWest.bind(this)
    }

    const handler = handlers[handle]
    return handler ? handler(box, x, y, aspectRatio) : box
  }

  /**
   * Constrain the crop box to stay within canvas bounds
   */
  constrainToCanvas(box: Rect, canvas: HTMLCanvasElement, handle: ResizeHandle, aspectRatio: number): Rect {
    let newBox = { ...box }

    if (newBox.x < 0) {
      newBox.x = 0
      if (handle === 'sw' || handle === 'nw' || handle === 'w') {
        newBox.width = box.x + box.width
        newBox.height = newBox.width / aspectRatio
        if (handle === 'nw') {
          newBox.y = box.y + box.height - newBox.height
        }
      }
    }
    if (newBox.x + newBox.width > canvas.width) {
      newBox.width = canvas.width - newBox.x
      newBox.height = newBox.width / aspectRatio
      if (handle === 'ne' || handle === 'nw') {
        newBox.y = box.y + box.height - newBox.height
      }
    }

    if (newBox.y < 0) {
      newBox.y = 0
      if (handle === 'ne' || handle === 'nw' || handle === 'n') {
        newBox.height = box.y + box.height
        newBox.width = newBox.height * aspectRatio
        if (handle === 'nw') {
          newBox.x = box.x + box.width - newBox.width
        }
      }
    }
    if (newBox.y + newBox.height > canvas.height) {
      newBox.height = canvas.height - newBox.y
      newBox.width = newBox.height * aspectRatio
      if (handle === 'sw' || handle === 'se') {
        newBox.x = box.x + box.width - newBox.width
      }
    }

    return newBox
  }

  private _getCornerHandles(box: Rect): Handle[] {
    return [
      { name: 'nw', x: box.x, y: box.y },
      { name: 'ne', x: box.x + box.width, y: box.y },
      { name: 'sw', x: box.x, y: box.y + box.height },
      { name: 'se', x: box.x + box.width, y: box.y + box.height }
    ]
  }

  private _getMidpointHandles(box: Rect): Handle[] {
    return [
      { name: 'n', x: box.x + box.width / 2, y: box.y },
      { name: 's', x: box.x + box.width / 2, y: box.y + box.height },
      { name: 'w', x: box.x, y: box.y + box.height / 2 },
      { name: 'e', x: box.x + box.width, y: box.y + box.height / 2 }
    ]
  }

  private _isPointInHandle(x: number, y: number, handle: Handle, size: number): boolean {
    return x >= handle.x - size / 2 && x <= handle.x + size / 2 && y >= handle.y - size / 2 && y <= handle.y + size / 2
  }

  private _isPointInMidpointHandle(x: number, y: number, handle: Handle): boolean {
    const isHorizontal = handle.name === 'n' || handle.name === 's'
    return isHorizontal
      ? x >= handle.x - this.HANDLE_SIZE / 2 && x <= handle.x + this.HANDLE_SIZE / 2 && y >= handle.y - 5 && y <= handle.y + 5
      : x >= handle.x - 5 && x <= handle.x + 5 && y >= handle.y - this.HANDLE_SIZE / 2 && y <= handle.y + this.HANDLE_SIZE / 2
  }

  private _resizeSoutheast(box: Rect, x: number, y: number, ratio: number): Rect {
    const width = x - box.x
    const height = width / ratio
    return { ...box, width, height }
  }

  private _resizeSouthwest(box: Rect, x: number, y: number, ratio: number): Rect {
    const width = box.x + box.width - x
    const height = width / ratio
    return { ...box, x, width, height }
  }

  private _resizeNortheast(box: Rect, x: number, y: number, ratio: number): Rect {
    const width = x - box.x
    const height = width / ratio
    const newY = box.y + box.height - height
    return { ...box, y: newY, width, height }
  }

  private _resizeNorthwest(box: Rect, x: number, y: number, ratio: number): Rect {
    const width = box.x + box.width - x
    const height = width / ratio
    const newX = x
    const newY = box.y + box.height - height
    return { ...box, x: newX, y: newY, width, height }
  }

  private _resizeSouth(box: Rect, x: number, y: number, ratio: number): Rect {
    const height = y - box.y
    const width = height * ratio
    return { ...box, width, height }
  }

  private _resizeNorth(box: Rect, x: number, y: number, ratio: number): Rect {
    const height = box.y + box.height - y
    const width = height * ratio
    const newY = y
    return { ...box, y: newY, width, height }
  }

  private _resizeEast(box: Rect, x: number, y: number, ratio: number): Rect {
    const width = x - box.x
    const height = width / ratio
    return { ...box, width, height }
  }

  private _resizeWest(box: Rect, x: number, y: number, ratio: number): Rect {
    const width = box.x + box.width - x
    const height = width / ratio
    const newX = x
    return { ...box, x: newX, width, height }
  }
}
